/**
 * VoiceUserCard Component
 * Displays individual user in voice channel with status and duration
 */

import React from 'react';
import type { VoiceParticipant } from '@/types/voice-session.types';
import { VoiceStatusIcon } from './VoiceStatusIcon';
import { getStatusRingClass } from './voiceStatus.utils';
import { useVoiceDuration, formatDuration } from '@/hooks/useVoiceDuration';

interface VoiceUserCardProps {
  participant: VoiceParticipant;
  isCurrentUser: boolean;
}

export const VoiceUserCard: React.FC<VoiceUserCardProps> = ({ participant, isCurrentUser }) => {
  const duration = useVoiceDuration(participant.joinedAt);

  return (
    <div
      className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-800/50 transition-colors group"
      data-testid="voice-user-card"
    >
      {/* Status Avatar */}
      <div className="relative">
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold text-white ${getStatusRingClass(participant.status, participant.isSpeaking)}`}
          title={getStatusTooltip(participant.status)}
        >
          {participant.displayName.charAt(0).toUpperCase()}
        </div>

        {/* Speaking pulse indicator */}
        {participant.isSpeaking && participant.status === 'speaking' && (
          <div className="absolute inset-0 rounded-full ring-2 ring-green-400 animate-ping" />
        )}
      </div>

      {/* Name */}
      <span
        className={`flex-1 text-sm truncate ${isCurrentUser ? 'font-semibold text-white' : 'text-gray-300'}`}
        title={participant.displayName}
      >
        {participant.displayName}
        {isCurrentUser && ' (Вие)'}
      </span>

      {/* Status Icon */}
      <VoiceStatusIcon status={participant.status} />

      {/* Duration */}
      <span
        className="text-xs text-gray-500 font-mono min-w-[40px] text-right"
        data-testid="voice-duration"
      >
        {formatDuration(duration)}
      </span>
    </div>
  );
};

function getStatusTooltip(status: VoiceParticipant['status']): string {
  switch (status) {
    case 'speaking':
      return 'Говори';
    case 'muted':
      return 'Без звук';
    case 'deafened':
      return 'Заглушен';
    case 'screen_sharing':
      return 'Споделя екран';
    case 'active':
      return 'Свързан';
  }
}
